import { FC, useCallback, useContext, useRef } from 'react';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { isEmpty } from 'lodash';
import { useBoolean, useCopyToClipboard, useTimeout } from 'usehooks-ts';
import CopyClipboard from '@ebsco-new-ui/copy-clipboard';
import { createBemBlockBuilder, TextArea } from '@ebsco-ui/ebsco-ui';

import { DelimitedList, Modal, ModalHeader } from '@app/components';
import { useAppLocale } from '@app/hooks';
import { getFormattedDate } from '@app/utils';
import { sharedMessages } from '@app/translations';

import { DashboardSearchModalContext } from './DashboardSearchModalContext';

import './DashboardSearchModal.scss';

const cnBem = createBemBlockBuilder(['dashboardSearchModal']);
const copyToClipboardTimeout = 3000;
const filtersLimit = 5;

export const DashboardSearchModal: FC = () => {
  const { locale } = useAppLocale();
  const [, copyToClipboard] = useCopyToClipboard();
  const { $t } = useIntl();
  const queryInputRef = useRef<HTMLTextAreaElement>(null);
  const {
    value: isCopiedMessageShown,
    setTrue: showCopiedMessage,
    setFalse: hideCopiedMessage,
  } = useBoolean(false);
  const {
    state: { isOpen, record },
    dispatch: updateModalState,
  } = useContext(DashboardSearchModalContext);
  const modalTitle = $t({
    id: 'dashboardSearchModal.searchDetails',
    defaultMessage: 'Search details',
  });
  const closeModal = useCallback(() => {
    updateModalState({ type: 'CLOSE_MODAL' });
  }, [updateModalState]);

  const handleCopyToClipboard = async () => {
    queryInputRef.current?.select();

    copyToClipboard(record?.query as string)
      .then(() => showCopiedMessage())
      .catch(error => console.error('Failed to copy!', error));
  };

  useTimeout(
    hideCopiedMessage,
    isCopiedMessageShown ? copyToClipboardTimeout : 0
  );

  return (
    <Modal
      isOpen={isOpen}
      className={cnBem()}
      modalTitle={modalTitle}
      shouldIncludeModalStyles={false}
      onClose={closeModal}
    >
      <main className={cnBem('__container')}>
        <ModalHeader
          title={modalTitle}
          titleLevel={1}
          className={cnBem('__header-title')}
          onClose={closeModal}
        />
        {record && (
          <Modal.Body>
            <dl>
              {record.query && (
                <form>
                  <TextArea
                    id="dashboard-search-modal-query"
                    readOnly
                    ref={queryInputRef}
                    value={record.query}
                    label={$t(sharedMessages.query)}
                  />
                  <CopyClipboard
                    className={cnBem('__copy-clipboard')}
                    copiedToClipboardFormattedMessage={$t(
                      sharedMessages.copiedToClipboard
                    )}
                    copyToClipboardFormattedMessage={$t(
                      sharedMessages.copyToClipboard
                    )}
                    showMessage={isCopiedMessageShown}
                    isDisabled={false}
                    onCopy={handleCopyToClipboard}
                  />
                </form>
              )}
              <dt data-auto="dashboard-search-modal-date-label">
                <FormattedMessage
                  id="dashboardSearchModal.date"
                  defaultMessage="Date"
                  tagName="h3"
                />
              </dt>
              <dd data-auto="dashboard-search-modal-date">
                {getFormattedDate({
                  date: record.timestamp,
                  locale,
                  shouldDisplayTime: false,
                })}
              </dd>
              <dt data-auto="dashboard-search-modal-results-label">
                <FormattedMessage
                  id="dashboardSearchModal.results"
                  defaultMessage="Results"
                  tagName="h3"
                />
              </dt>
              <dd data-auto="dashboard-search-modal-results">
                <FormattedNumber value={record.totalRecords} />
              </dd>
              <dt data-auto="dashboard-search-modal-search-type-label">
                <FormattedMessage
                  id="dashboardSearchModal.searchType"
                  defaultMessage="Search type"
                  tagName="h3"
                />
              </dt>
              <dd data-auto="dashboard-search-modal-search-type">
                {record.option}
              </dd>
              {!isEmpty(record.filters) && (
                <>
                  <dt data-auto="dashboard-search-modal-filters-label">
                    <FormattedMessage
                      id="dashboardSearchModal.filters"
                      defaultMessage="Filters"
                      tagName="h3"
                    />
                  </dt>
                  <dd data-auto="dashboard-search-modal-filters">
                    <DelimitedList
                      items={record.filters}
                      limit={filtersLimit}
                    />
                  </dd>
                </>
              )}
            </dl>
            <div className={cnBem('__link-container')}>
              <Link className={cnBem('__link')} to={record.link}>
                <FormattedMessage
                  id="dashboardSearchModal.viewResults"
                  defaultMessage="View results"
                />
              </Link>
            </div>
          </Modal.Body>
        )}
      </main>
    </Modal>
  );
};
